import { useQuery } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { useEffect, useMemo, useRef, useState } from 'react'
import { teamQueries, type TeamListParams } from '@/api/endpoints/teams'
import type { TeamListItem } from '@/api/generated/types'
import { apiErrorMessage } from '@/api/errors'
import { DataTable } from '@/components/kit/data-table'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toTeamListParams, type TeamsListSearch } from '@/features/teams/team-list-search'
import { formatDisplayDateTime } from '@/lib/dates'
import { searchDraftForUrl } from '@/lib/list-search'
import { SERVER_OPTIONS } from '@/lib/servers'

const teamsRoute = getRouteApi('/_authed/teams')

export const Route = createFileRoute('/_authed/teams/')({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.prefetchQuery(teamQueries.list(toTeamListParams(deps))),
  component: TeamsPage,
})

const columns: ColumnDef<TeamListItem>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link
        to="/teams/$uuid"
        params={{ uuid: row.original.uuid! }}
        search
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    accessorKey: 'tag',
    header: 'Tag',
  },
  {
    accessorKey: 'server',
    header: 'Server',
    cell: ({ row }) => row.original.server ?? '—',
  },
  {
    id: 'updated_at',
    header: 'Updated',
    cell: ({ row }) => formatDisplayDateTime(row.original.updated_at),
  },
]

function TeamsPage() {
  const search: TeamsListSearch = teamsRoute.useSearch()
  const navigate = useNavigate({ from: '/teams' })
  const params: TeamListParams = toTeamListParams(search)
  const { data, isLoading, isPlaceholderData, isError, error } = useQuery(teamQueries.list(params))
  const [draft, setDraft] = useState(search.query ?? '')
  const submittedRef = useRef(search.query ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    [],
  )

  useEffect(() => {
    const nextDraft = searchDraftForUrl(search.query, submittedRef.current)
    if (nextDraft === undefined) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    submittedRef.current = nextDraft
    setDraft(nextDraft)
  }, [search.query])

  const pagination = useMemo(
    () => ({
      page: search.page,
      displayPage: isPlaceholderData ? (data?.current ?? search.page) : search.page,
      pageSize: search.page_size,
      pages: data?.pages ?? 0,
      total: data?.total ?? 0,
      onPageChange: (page: number) => void navigate({ search: (prev) => ({ ...prev, page }) }),
      onPageSizeChange: (page_size: number) =>
        void navigate({ search: (prev) => ({ ...prev, page_size, page: 1 }) }),
    }),
    [data, isPlaceholderData, search.page, search.page_size, navigate],
  )

  const hasFilters = Boolean(
    search.query || search.server || search.inactive || search.without_members,
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-2xl font-semibold">Teams</h1>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="max-w-sm"
          placeholder="Search…"
          value={draft}
          aria-label="Search teams"
          onChange={(e) => {
            const value = e.target.value
            setDraft(value)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
              submittedRef.current = value
              void navigate({
                search: (prev) => ({ ...prev, query: value || undefined, page: 1 }),
                replace: true,
              })
            }, 300)
          }}
        />
        <Select
          value={search.server ?? 'all'}
          onValueChange={(value) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                server: value === 'all' ? undefined : (value as typeof search.server),
                page: 1,
              }),
            })
          }
        >
          <SelectTrigger className="w-40" aria-label="Filter by server">
            <SelectValue placeholder="Server" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All servers</SelectItem>
            {SERVER_OPTIONS.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Checkbox
            id="teams-inactive"
            checked={search.inactive === true}
            onCheckedChange={(checked) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  inactive: checked === true ? true : undefined,
                  page: 1,
                }),
              })
            }
          />
          <Label htmlFor="teams-inactive">Include inactive</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="teams-without-members"
            checked={search.without_members === true}
            onCheckedChange={(checked) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  without_members: checked === true ? true : undefined,
                  page: 1,
                }),
              })
            }
          />
          <Label htmlFor="teams-without-members">Without any membership</Label>
        </div>
        {hasFilters && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (debounceRef.current) clearTimeout(debounceRef.current)
              submittedRef.current = ''
              setDraft('')
              void navigate({
                search: (prev) => ({
                  ...prev,
                  query: undefined,
                  server: undefined,
                  inactive: undefined,
                  without_members: undefined,
                  page: 1,
                }),
              })
            }}
          >
            Clear filters
          </Button>
        )}
      </div>
      <DataTable
        columns={columns}
        data={isError ? [] : (data?.results ?? [])}
        getRowId={(team) => team.uuid ?? ''}
        pagination={pagination}
        isLoading={isLoading}
        errorMessage={isError ? apiErrorMessage(error) : undefined}
        emptyMessage="No teams match these filters"
      />
    </div>
  )
}
